import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Table from 'react-bootstrap/Table';

import Text from './Text';
import Box from './layout/Box';
import BookImage from './BookImage';
import { useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';

const StyledBox = styled(Box)`
  flex-direction: column;
  align-items: center;
  flex-grow: 1;

  @media (min-width: calc(769px + ${({ leftNavbarOpen }) =>
      leftNavbarOpen ? 300 : 0}px)) {
    flex-direction: row;
    align-items: flex-start;
  }
`;

const StyledImageBox = styled(Box)`
  width: 100%;
  min-width: 0;
  justify-content: center;
  background-color: ${({ theme }) => theme.colors['mainBackgroundColor']};

  @media (min-width: calc(769px + ${({ leftNavbarOpen }) =>
      leftNavbarOpen ? 300 : 0}px)) {
    width: auto;
    flex-shrink: 0;

    background-color: ${({ theme }) => theme.colors['transparentColor']};
  }
`;

const StyledImageWrapper = styled(Box)`
  width: 100%;
  min-width: 0;
  justify-content: center;
  flex-direction: column;

  @media (min-width: calc(769px + ${({ leftNavbarOpen }) =>
      leftNavbarOpen ? 300 : 0}px)) {
    width: auto;
    flex-shrink: 0;
  }
`;

const StyledButtonsWrapper = styled(Box)`
  margin-left: 4px;

  @media (min-width: calc(769px + ${({ leftNavbarOpen }) =>
      leftNavbarOpen ? 300 : 0}px)) {
    margin-left: 0px;
    align-items: center;
    justify-content: center;
  }
`;

const StyledTableBox = styled(Box)`
  width: 100%;

  @media (min-width: calc(769px + ${({ leftNavbarOpen }) =>
      leftNavbarOpen ? 300 : 0}px)) {
    width: auto;
    padding-left: 10px;
  }
`;

const ProductPreviewCard = ({
  product,
  withLargeTitle,
  onNewClick,
  showButtons,
}) => {
  const [showDescription, setShowDescription] = React.useState(false);

  const leftNavbarOpen = useSelector(
    (state) => state.navigation.leftNavbarOpen,
  );

  if (!product) {
    return null;
  }

  const fullTitle =
    product.title + (product.subtitle ? ` - ${product.subtitle}` : '');

  const { thumbnail } = product;

  // Table keys are title, subtitle, author, isbn13, isbn10, publisher, pages, edition, language, description
  const tableKeys = [
    {
      key: 'title',
      name: 'Titel',
    },
    {
      key: 'subtitle',
      name: 'Undertitel',
    },
    {
      key: 'author',
      name: 'Författare',
    },
    {
      key: 'isbn',
      name: 'ISBN13',
    },
    {
      key: 'isbn10',
      name: 'ISBN10',
    },
    {
      key: 'publisher',
      name: 'Förlag',
    },
    {
      key: 'pages',
      name: 'Sidor',
    },
    {
      key: 'edition',
      name: 'Utgåva',
    },
    {
      key: 'language',
      name: 'Språk',
      formatter: (language) => {
        if (language === 'sv') {
          return <FormattedMessage defaultMessage="Svenska" />;
        } else if (language === 'en') {
          return <FormattedMessage defaultMessage="Engelska" />;
        }

        return undefined;
      },
    },
  ];

  const descriptionTeaserLength = 200;

  let description;

  if (product.description) {
    description = showDescription
      ? product?.description
      : product?.description?.substring(0, descriptionTeaserLength) + '...';
  }

  return (
    <Box className="flex-column">
      {withLargeTitle && (
        <Box
          className="mb-3 flex-column pb-3"
          style={{
            borderBottom: '2px solid #e7e7e7',
          }}
        >
          <Text variant="sectionTitle">Artikel</Text>
          <Text variant="sectionSubtitle" className="mt-1">
            {fullTitle}
          </Text>
        </Box>
      )}

      {showButtons && (
        <Box
          className="mb-3 flex-column pb-3"
          style={{
            borderBottom: '2px solid #e7e7e7',
          }}
        >
          <StyledButtonsWrapper leftNavbarOpen={leftNavbarOpen}>
            <Box
              onClick={onNewClick}
              style={{
                width: '100%',
                cursor: 'pointer',
              }}
            >
              <Text
                style={{
                  fontSize: '0.7rem',
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  textAlign: 'center',
                }}
                color="primaryColor"
              >
                <FormattedMessage defaultMessage={'Sälj den här boken'} />
              </Text>
            </Box>
          </StyledButtonsWrapper>
        </Box>
      )}

      <StyledBox leftNavbarOpen={leftNavbarOpen}>
        <StyledImageWrapper leftNavbarOpen={leftNavbarOpen}>
          <StyledImageBox leftNavbarOpen={leftNavbarOpen} className="mb-3">
            <BookImage bookId={product.id} source={thumbnail} size={150} />
          </StyledImageBox>
        </StyledImageWrapper>

        <StyledTableBox leftNavbarOpen={leftNavbarOpen} className="flex-column">
          <Table
            borderless
            size="sm"
            style={{
              marginBottom: 0,
            }}
          >
            <tbody>
              {tableKeys.map(({ key, name, formatter }) => {
                const value = product[key];

                if (!value) {
                  return null;
                }

                return (
                  <tr key={key}>
                    <td
                      style={{
                        textAlign: 'left',
                        paddingRight: 10,
                      }}
                    >
                      <Text className="text-muted">{name}</Text>
                    </td>
                    <td style={{ textAlign: 'left' }}>
                      <Text>{formatter ? formatter(value) : value}</Text>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>

          {description && showButtons && (
            <Box className="flex-column align-items-start">
              <Text
                style={{
                  padding: 4,
                  color: '#6c757d',
                }}
              >
                <FormattedMessage defaultMessage="Beskrivning" />
              </Text>
              <Text
                style={{
                  padding: 4,
                  textAlign: 'left',
                }}
              >
                {description}
              </Text>

              {product?.description &&
                product?.description?.length > descriptionTeaserLength && (
                  <Box className="w-100 mt-2 justify-content-center">
                    <Box
                      className="px-3 py-1"
                      borderColor="primaryColor"
                      borderRadius={999}
                      borderWidth={1}
                      style={{
                        padding: 4,
                        cursor: 'pointer',
                      }}
                      onClick={() => {
                        setShowDescription(!showDescription);
                      }}
                    >
                      <Text color="primaryColor">
                        {showDescription ? 'Stäng' : 'Läs mer'}
                      </Text>
                    </Box>
                  </Box>
                )}
            </Box>
          )}
        </StyledTableBox>
      </StyledBox>
    </Box>
  );
};

ProductPreviewCard.defaultProps = {
  useTitle: false,
  after: undefined,
  sectionTitle: undefined,
  withLargeTitle: true,
  product: undefined,
  showButtons: true,
};

ProductPreviewCard.propTypes = {
  product: PropTypes.object,
  withLargeTitle: PropTypes.bool,
  useTitle: PropTypes.bool,
  after: PropTypes.node,
  sectionTitle: PropTypes.node,
  showButtons: PropTypes.bool,
};

export default ProductPreviewCard;
